import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';
import Navbar from '../components/Navbar';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TermsAndConditions = () => {
  const navigate = useNavigate();
  const [dynamicContent, setDynamicContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/site-content/terms-and-conditions`)
      .then(res => { if (res.data.content) setDynamicContent(res.data.content); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const Section = ({ number, title, children }) => (
    <div className="mb-10">
      <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-start gap-3">
        <span className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-bold mt-0.5">
          {number}
        </span>
        {title}
      </h2>
      <div className="pl-11 space-y-3 text-slate-700 leading-relaxed">{children}</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white pt-16">
      <Navbar />

      <div className="px-6 md:px-12 lg:px-24 pt-6">
        <div className="max-w-3xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-slate-500 hover:text-slate-900 transition-colors text-sm font-medium"
            data-testid="back-from-terms"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        </div>
      </div>

      <article className="px-6 md:px-12 lg:px-24 py-8">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-slate-900" data-testid="terms-title">Terms & Conditions</h1>
              <p className="text-slate-500 text-sm">Proptick</p>
            </div>
          </div>

          <div className="border-t border-slate-100 pt-8">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : dynamicContent ? (
              <div className="prose-slate">
                {dynamicContent.split('\n').map((line, i) => {
                  const trimmed = line.trim();
                  if (!trimmed) return null;
                  const numbered = trimmed.match(/^(\d+)\.\s+(.+)/);
                  if (numbered) {
                    return (
                      <h2 key={i} className="text-xl font-bold text-slate-900 mb-4 mt-10 flex items-start gap-3">
                        <span className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-bold mt-0.5">{numbered[1]}</span>
                        {numbered[2]}
                      </h2>
                    );
                  }
                  if (trimmed.startsWith('- ') || trimmed.startsWith('• ')) {
                    return <li key={i} className="text-slate-600 ml-11 list-disc">{trimmed.slice(2)}</li>;
                  }
                  return <p key={i} className="text-slate-700 leading-relaxed mb-3 pl-11">{trimmed}</p>;
                })}
              </div>
            ) : (
              <>

            <Section number="1" title="Introduction">
              <p>These Terms and Conditions ("Terms") govern your access to and use of the Proptick platform, including our website, mobile applications, and related services (collectively, the "Platform").</p>
              <p>Proptick is operated by a company headquartered in Dubai, United Arab Emirates, and provides property listing and discovery services, including listings in Kerala, India and other regions.</p>
              <p>By accessing or using the Platform, you agree to be legally bound by these Terms.</p>
            </Section>

            <Section number="2" title="Nature of Platform">
              <p>Proptick is a technology-enabled marketplace that connects property seekers with property owners, developers, and agents.</p>
              <ul className="list-disc list-inside space-y-1 text-slate-600">
                <li>We do not own, sell, or lease properties listed on the Platform</li>
                <li>We do not guarantee the accuracy or legality of listings</li>
                <li>Transactions occur directly between users</li>
              </ul>
              <p className="text-sm text-slate-500 italic mt-2">This aligns with industry practice where platforms disclaim responsibility for listing accuracy and user transactions.</p>
            </Section>

            <Section number="3" title="Eligibility">
              <p>You must:</p>
              <ul className="list-disc list-inside space-y-1 text-slate-600">
                <li>Be at least 18 years old</li>
                <li>Have legal capacity to enter into binding agreements</li>
                <li>Provide accurate and complete information</li>
              </ul>
              <p className="mt-3">If you are acting as an agent, broker, or developer, you must comply with all applicable licensing laws in UAE or India (Kerala).</p>
            </Section>

            <Section number="4" title="User Accounts">
              <ul className="list-disc list-inside space-y-1 text-slate-600">
                <li>You are responsible for maintaining confidentiality of login credentials</li>
                <li>You must notify us immediately of unauthorized access</li>
                <li>We reserve the right to suspend or terminate accounts for violations</li>
              </ul>
            </Section>

            <Section number="5" title="Listing Policy & Content Standards">
              <p>If you post a property listing, you agree that:</p>
              <ul className="list-disc list-inside space-y-1 text-slate-600">
                <li>You have legal authority to list the property</li>
                <li>All information is accurate, complete, and up to date</li>
                <li>The property is genuinely available</li>
                <li>Content is not misleading, unlawful, or offensive</li>
              </ul>
              <p className="mt-4">We may remove or suspend listings if:</p>
              <ul className="list-disc list-inside space-y-1 text-slate-600">
                <li>Property is sold, rented, or unavailable</li>
                <li>Information is false or misleading</li>
                <li>Listing violates applicable laws</li>
              </ul>
              <p className="text-sm text-slate-500 italic mt-2">This mirrors industry safeguards against fake/misleading listings.</p>
            </Section>

            <Section number="6" title="Prohibited Activities">
              <p>You agree NOT to:</p>
              <ul className="list-disc list-inside space-y-1 text-slate-600">
                <li>Post false, misleading, or fraudulent listings</li>
                <li>Use bots, scraping tools, or automated systems</li>
                <li>Interfere with platform functionality</li>
                <li>Impersonate another person or entity</li>
                <li>Use the platform for unlawful purposes</li>
              </ul>
              <p className="text-sm text-slate-500 italic mt-2">Platforms typically restrict scraping, misuse, and unlawful conduct to protect system integrity.</p>
            </Section>

            <Section number="7" title="Transactions and User Responsibility">
              <p>Proptick is not a party to any transaction between users.</p>
              <p>Users must independently verify:</p>
              <ul className="list-disc list-inside space-y-1 text-slate-600">
                <li>Property ownership</li>
                <li>Legal documentation</li>
                <li>Pricing and terms</li>
              </ul>
              <p className="mt-3">We strongly recommend:</p>
              <ul className="list-disc list-inside space-y-1 text-slate-600">
                <li>Site visits</li>
                <li>Legal due diligence</li>
                <li>Verification of agents/brokers</li>
              </ul>
            </Section>

            <Section number="8" title="Fees, Subscriptions & Payments">
              <ul className="list-disc list-inside space-y-1 text-slate-600">
                <li>Certain services (e.g., premium listings, visibility boosts) may be paid</li>
                <li>Fees must be paid in advance unless stated otherwise</li>
                <li>Payments are non-refundable, except in cases of duplicate transactions</li>
              </ul>
              <p className="text-sm text-slate-500 italic mt-2">This follows standard marketplace models where services activate only after payment.</p>
            </Section>

            <Section number="9" title="Intellectual Property">
              <ul className="list-disc list-inside space-y-1 text-slate-600">
                <li>All Platform content (excluding user listings) is owned by Proptick</li>
                <li>By uploading content, you grant Proptick a worldwide, royalty-free license to use, display, and promote it</li>
              </ul>
            </Section>

            <Section number="10" title="Data Protection & Privacy">
              <p>Use of the Platform is subject to our <a href="/privacy-policy" className="text-blue-600 font-medium hover:underline">Privacy Policy</a>.</p>
              <p>You agree:</p>
              <ul className="list-disc list-inside space-y-1 text-slate-600">
                <li>To obtain consent before sharing third-party data</li>
                <li>That your data may be processed across jurisdictions (UAE, India, etc.)</li>
              </ul>
            </Section>

            <Section number="11" title="Limitation of Liability">
              <p>To the maximum extent permitted by law:</p>
              <p className="mt-2">Proptick shall not be liable for:</p>
              <ul className="list-disc list-inside space-y-1 text-slate-600">
                <li>Any inaccuracies in listings</li>
                <li>Loss arising from transactions between users</li>
                <li>Platform downtime or technical issues</li>
                <li>Loss of profit, data, or business</li>
              </ul>
              <p className="text-sm text-slate-500 italic mt-2">Marketplace platforms typically exclude liability for indirect losses and reliance on content.</p>
            </Section>

            <Section number="12" title="Indemnity">
              <p>You agree to indemnify and hold Proptick harmless from:</p>
              <ul className="list-disc list-inside space-y-1 text-slate-600">
                <li>Claims arising from your listings</li>
                <li>Breach of these Terms</li>
                <li>Violation of applicable laws</li>
              </ul>
            </Section>

            <Section number="13" title="Suspension and Termination">
              <p>We may suspend or terminate your access if:</p>
              <ul className="list-disc list-inside space-y-1 text-slate-600">
                <li>You violate these Terms</li>
                <li>You provide false information</li>
                <li>Required payments are not made</li>
              </ul>
              <p className="mt-3">We may also remove listings at our discretion.</p>
            </Section>

            <Section number="14" title="Cross-Border Use (UAE & India)">
              <ul className="list-disc list-inside space-y-1 text-slate-600">
                <li>Proptick operates from the UAE but may host listings from India (including Kerala)</li>
                <li>Users must comply with local real estate laws in their jurisdiction</li>
                <li>Proptick is not responsible for regulatory compliance of listings</li>
              </ul>
            </Section>

            <Section number="15" title="Third-Party Services">
              <p>The Platform may include:</p>
              <ul className="list-disc list-inside space-y-1 text-slate-600">
                <li>Third-party tools</li>
                <li>External links</li>
                <li>Financial or legal service providers</li>
              </ul>
              <p className="mt-3">We are not responsible for third-party services or their policies.</p>
            </Section>

            <Section number="16" title="Amendments">
              <p>We may update these Terms at any time. Continued use of the Platform constitutes acceptance of updated Terms.</p>
            </Section>

            <Section number="17" title="Governing Law & Jurisdiction">
              <p>These Terms shall be governed by the laws of the United Arab Emirates.</p>
              <p>Any disputes shall be subject to the jurisdiction of the courts of Dubai, UAE, unless otherwise required by applicable law.</p>
            </Section>

            <Section number="18" title="Contact Information">
              <div className="bg-slate-50 rounded-xl p-6 border border-slate-100">
                <p className="font-semibold text-slate-900 text-lg">Proptick</p>
                <p className="text-slate-600">Dubai, United Arab Emirates</p>
                <p className="text-slate-600 mt-1">
                  Email: <a href="mailto:support@proptick.com" className="text-blue-600 font-medium hover:underline">support@proptick.com</a>
                </p>
              </div>
            </Section>

              </>
            )}
          </div>
        </div>
      </article>
    </div>
  );
};

export default TermsAndConditions;
